import { useContext, useEffect, useState } from "react";
import { useOutletContext } from "react-router";
import styled from "styled-components";
import { AppQueryClient } from "../../../../api/queryClient";
import AppContext from "../../../../AppContext";
import { APP_TITLE } from "../../../../config/constant";
import BtnLargeSolid from "../../../common/buttons/BtnLargeSolid";
import FormContainer from "../../../common/forms/FormContainer";
import FormField from "../../../common/forms/FormField";
import ListComponent, { StatusBadge } from "../../../common/lists/ListComponent";
import ContainerLoader from "../../../common/loaders/ContainerLoader";
import MainWindowHeader, { CustomSearch } from "../../../common/mainApp/mainWindowHeaders/MainWindowHeader";

// #region SCOPED CSS
const StyledPage = styled.div`
    &.my-defaults-genders-page {
        display: flex;
        flex-direction: column;
    }

    .add-gender-row {
        display: flex;
        align-items: flex-end;
        gap: 16px;
        width: 100%;
    }

    .add-gender-system-name {
        flex: 0 0 254px;
        min-width: 0;
    }

    .add-gender-description {
        flex: 1 1 auto;
        min-width: 320px;
    }

    .add-gender-button {
        width: 64px;
        height: 37px;
        flex-shrink: 0;
    }

    .table-action-icon {
        width: 16px;
        height: 16px;
        color: var(--text-icons-gray-100);
    }

    @media (max-width: 900px) {
        .add-gender-row {
            flex-wrap: wrap;
        }

        .add-gender-system-name,
        .add-gender-description {
            flex-basis: 100%;
        }
    }

    .rdt_Pagination {
        display: none !important;
    }

    .table-structure > div:nth-child(2) {
        height: 24px;
    }
`;
// #endregion

export default function MyDefaultGendersPage() {
    document.title = `${APP_TITLE} - My Default Genders`;

    // #region VARS
    const { isLoadingApp } = useContext(AppContext);
    const { MainLayoutProps, layoutType } = useOutletContext();

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };
    // #endregion

    // #region STATES
    //----- Header Filters -----
    const [allRetailBrandsList, setAllRetailBrandsList] = useState([]);
    const [allPOSList, setAllPOSList] = useState([]);
    const [selectedRetailBrand, setSelectedRetailBrand] = useState(null);
    const [filteredPOS, setFilteredPOS] = useState(undefined);
    const [requestsEnabled, setRequestsEnabled] = useState(false);
    const [searchValue, setSearchValue] = useState("");

    const filters = {
        filterRetailBrandIDs: selectedRetailBrand ? [selectedRetailBrand?.value] : [],
        filterPOSIDs: filteredPOS?.map(item => item?.value)
        //searchText: searchText
    };

    // #endregion

    // #region LIST COMPONENT VARS
    const [listComponentIsLoading, setListComponentIsLoading] = useState(false);
    const [listNumRows, setListNumRows] = useState(50);
    const listQueryKey = ["MyDefaultGendersList", filters];
    const listColumns = [
        {
            name: "Gender name",
            selector: row => row?.genderName ?? "--"
        },
        {
            name: "Gender name ID",
            selector: row => row?.genderNameID || "--"
        },
        {
            name: "Gender",
            selector: row => row?.gender || "--"
        },
        {
            name: "Genders count",
            selector: row => row?.gendersCount || "--"
        },
        {
            name: "Issues",
            selector: row => row?.issues || "--"
        },
        {
            name: "Created by",
            selector: row => row?.createdBy || "--"
        },
        {
            name: "Created on",
            selector: row => row?.createdOn || "--"
        },
        {
            name: "Status",
            selector: row => <StatusBadge statusValue={row?.status ?? "--"} colorValue={"#00bb00"} />
        }
    ];

    const mapListData = data => {
        // USING HARDCODED DATA while this draft still points to getStoresData.
        /* const GENDER_SYSTEMS_TEST_DATA = [
            {
                _id: "gender-system-2007588",
                genderName: "Black",
                genderNameID: "2007588",
                gender: "[list of genders], [+#]",
                gendersCount: "11",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            },
            {
                _id: "gender-system-258",
                genderName: "Rosa",
                genderNameID: "258",
                gender: "[list of genders], [+#]",
                gendersCount: "10",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            },
            {
                _id: "gender-system-1604408",
                genderName: "Null",
                genderNameID: "1604408",
                gender: "[list of genders], [+#]",
                gendersCount: "10",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            },
            {
                _id: "gender-system-102926",
                genderName: "Gull",
                genderNameID: "102926",
                gender: "[list of genders], [+#]",
                gendersCount: "8",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            },
            {
                _id: "gender-system-102925",
                genderName: "Solv",
                genderNameID: "102925",
                gender: "[list of genders], [+#]",
                gendersCount: "10",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            },
            {
                _id: "gender-system-100584",
                genderName: "Hvit",
                genderNameID: "100584",
                gender: "[list of genders], [+#]",
                gendersCount: "10",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            },
            {
                _id: "gender-system-100583",
                genderName: "Orange",
                genderNameID: "100583",
                gender: "[list of genders], [+#]",
                gendersCount: "8",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            },
            {
                _id: "gender-system-100582",
                genderName: "Lilla",
                genderNameID: "100582",
                gender: "[list of genders], [+#]",
                gendersCount: "39",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            },
            {
                _id: "gender-system-100581",
                genderName: "Gronn",
                genderNameID: "100581",
                gender: "[list of genders], [+#]",
                gendersCount: "17",
                issues: "-",
                createdBy: "-",
                createdOn: "-",
                status: "Synced"
            }
        ]; */

        const testData = data?.map(item => {
            return {
                genderName: item,
                //genderNameID: item?.id,
                //sizes: item?.sizeNames?.join(", "),
                //sizeCount: item?.sizeNames?.length,
                status: "Synced"
            };
        });

        return testData;
    };
    // #endregion

    // #region METHODS
    const onSearch = value => {
        setSearchValue(value || "");
    };

    const onRefreshAction = () => {
        AppQueryClient.invalidateQueries(listQueryKey);
    };

    const onFiltersLoaded = data => {
        const defaultRetailBrandValue =
            sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand");

        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map(item => {
                const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
                let retailBrandName = item?.name;

                if (item?._id === defaultRetailBrandID) {
                    retailBrandName = `${item?.name} (default)`;
                }

                return {
                    name: retailBrandName,
                    value: item?._id,
                    selected: item?._id === defaultRetailBrandValue
                };
            });

            const defaultRetailBrandObj =
                retailBrands?.find(item => item?.value === defaultRetailBrandValue) || retailBrands?.[0];

            setAllRetailBrandsList(retailBrands);
            setSelectedRetailBrand(defaultRetailBrandObj);
        }

        if (data?.posSystems?.length) {
            const formFields = data?.posSystems?.map(item => ({
                ...item,
                name: item.name,
                value: item._id,
                selected: true
            }));

            setAllPOSList(formFields);
            setFilteredPOS(formFields);
        }

        setRequestsEnabled(true);
    };

    const onRetailBrandChange = selection => {
        setSelectedRetailBrand(selection);
        const currentRetailBrandID = selection?.value;
        sessionStorage.setItem("currentRetailBrand", currentRetailBrandID || "");
    };

    const onSelectedPOSChanged = (selections, dropdownDataWithSelections) => {
        setFilteredPOS(selections);
        setAllPOSList(dropdownDataWithSelections);
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setupHeaders({
            props,
            searchValue,
            onSearch,
            onRefreshAction,
            allRetailBrandsList,
            onRetailBrandChange,
            selectedRetailBrand,
            allPOSList,
            onSelectedPOSChanged,
            onFiltersLoaded
        });
    }, [allRetailBrandsList, selectedRetailBrand, allPOSList]);
    // #endregion

    // #region RENDER
    return (
        <StyledPage className="my-defaults-genders-page">
            <FormContainer
                title={
                    <div>
                        DEFAULT GENDER DETAILS -{" "}
                        <span className="text-orange-500">NOT POSSIBLE WITH FRONT SYSTEMS</span>
                    </div>
                }
                showDefaultRightLegends={true}>
                <div className="add-gender-row">
                    <div className="add-gender-system-name">
                        <FormField
                            type="text"
                            title="Gender name"
                            placeholder="Insert gender name"
                            requiredType="disabledSimple"
                            varName="genderNameName"
                            tooltip="Gender name"
                        />
                    </div>

                    <div className="add-gender-description">
                        <FormField
                            type="text"
                            title="Gender VARIANTS"
                            placeholder="Insert variants' names + ENTER"
                            requiredType="disabledSimple"
                            varName="genderNameDescription"
                            tooltip="Gender variants"
                        />
                    </div>

                    <BtnLargeSolid
                        className="add-gender-button"
                        text="ADD"
                        disabled={true}
                        normalColor="var(--Buttons-All-Platforms-Gray-500)"
                        hoverColor="var(--Buttons-All-Platforms-Gray-500)"
                        hoverBorderColor="var(--text-icons-orange-500)"
                        disabledColor="var(--buttons-all-platforms-gray-500, #10141b)"
                        textStyle={{
                            fontSize: "11px",
                            textTransform: "uppercase"
                        }}
                    />
                </div>
            </FormContainer>

            <ContainerLoader
                isLoading={!isLoadingApp && listComponentIsLoading}
                className="w-100"
                isLoadingOverlay={true}
                addBlurFilters={false}>
                <ListComponent
                    className="w-100"
                    tableClassName="expandedRowsCentered"
                    title="DEFAULT GENDERS - AS IN POS"
                    columns={listColumns}
                    api={{
                        useBOstageXAPI: true,
                        BOstageXAPIEndpoint: "X/Products/getProductGendersFromPOS",
                        formData: {
                            retailBrandID: selectedRetailBrand?.value || "",
                            limit: listNumRows,
                            ...filters
                        },
                        onSuccess: mapListData
                    }}
                    selectableRows={true}
                    showLoader={false}
                    onListLoading={loading => setListComponentIsLoading(loading)}
                    pageLimit={listNumRows}
                    currentPage={1}
                    queryKey={listQueryKey}
                    requestEnabled={requestsEnabled}
                    onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                    actions={{
                        includeActions: true,
                        editIsVisible: true,
                        deleteIsVisible: true,
                        suspendIsVisible: false,
                        actionColumnWidth: "100px",
                        actionColumnTitle: "Actions"
                    }}
                />
            </ContainerLoader>
        </StyledPage>
    );
    // #endregion
}

// #region HEADERS
function setupHeaders({
    props,
    searchValue,
    onSearch,
    onRefreshAction,
    allRetailBrandsList,
    onRetailBrandChange,
    selectedRetailBrand,
    allPOSList,
    onSelectedPOSChanged,
    onFiltersLoaded
}) {
    props.setControlBarLeftBtns([
        {
            customSearch: (
                <CustomSearch
                    enabled={false}
                    placeholder="Search"
                    onTextChange={onSearch}
                    searchValue={searchValue}
                    onClearSearch={() => onSearch("")}
                />
            )
        }
    ]);

    props.setControlBarCenterBtns([
        { to: "/products", active: false, disabled: false, title: "LIVE" },
        { to: "/products/my-defaults/seasons", active: true, disabled: false, title: "MY DEFAULTS" }
    ]);

    props.setControlBarRightBtns([
        {
            to: "/products/my-defaults/seasons",
            active: false,
            disabled: false,
            title: "Seasons",
            icon: "snowflake-02"
        },
        {
            to: "/products/my-defaults/genders",
            active: true,
            disabled: false,
            title: "Genders",
            icon: "link-05"
        },
        {
            to: "/products/my-defaults/colors",
            active: false,
            disabled: false,
            title: "Colors",
            icon: "palette"
        },
        {
            to: "/products/my-defaults/categories",
            active: false,
            disabled: false,
            title: "Categ",
            icon: "grid-01"
        },
        {
            to: "/products/my-defaults/sizes",
            active: false,
            disabled: false,
            title: "Sizes",
            icon: "ruler"
        }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    useLayoutButton: {
                        useInsteadOfGridList: true,
                        text: "SAVE",
                        enabled: false,
                        onClick: undefined
                    },
                    gridEnabled: false,
                    listEnabled: false
                }}
                search={{
                    visible: false
                }}
                onRefreshClick={onRefreshAction}
                firstOrganize={{
                    visible: true,
                    dropdownData: allRetailBrandsList,
                    onDropdownChange: onRetailBrandChange,
                    disabled: false,
                    type: "singleSelect",
                    selectedValue: selectedRetailBrand?.name
                }}
                secondOrganize={{
                    visible: true,
                    dropdownData: allPOSList,
                    onDropdownChange: onSelectedPOSChanged,
                    disabled: false,
                    type: "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all POS",
                        labelAllSelected: "All POS",
                        labelNoneSelected: "--",
                        allItems: allPOSList
                    }
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true,
                        usePOSSystems: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                settingsButton={{
                    visible: true,
                    enabled: false
                }}
            />
        ),
        visible: false,
        gridEnabled: false,
        listEnabled: false,
        onRefreshAction
    });
}
// #endregion

// #region SMALL COMPONENTS

// #endregion
